import {
  FleetAssetCapabilityType,
  fleetAssetCapabilityTypes,
  fleetAssetCapabilityTypesSelector,
  useGetVehicleCapabilitiesQuery,
  VehicleEntity
} from "@ots-frontend/ots-data";
import {Card, Divider, Grid, Group, Text, Title} from "@mantine/core";
import {useTranslation} from "react-i18next";
import {ObjectMappingDataTableCard} from "../../card/ObjectMappingDataTableCard/ObjectMappingDataTableCard";
import {HasAccess} from "../../auth/HasAccess/HasAccess";
import {DefaultText} from "../../text/DefaultText/DefaultText";
import {
  FleetAssetCapabilityDataTable
} from "../../data-table/FleetAssetCapabilityDataTable/FleetAssetCapabilityDataTable";
import {useCallback, useMemo} from "react";
import {useVehicleMutation} from "../../../hooks/useVehicleMutation";
import {PAGINATION_PAGE_SIZES} from "../../data-table/DataTable/usePagination";

export type VehicleDetailsProps = {
  vehicle: VehicleEntity
}

export const VehicleDetails = (props: VehicleDetailsProps) => {
  const {vehicle} = props;

  const {t} = useTranslation(["common"]);

  return (
    <Grid>
      <Grid.Col span={6}>
        <Card shadow="sm" padding="lg" radius="md" withBorder>

          <Group position="apart" pb="sm">
            <Title order={4}>{t("profile.vehicle.info.section.details.label", "Details")}</Title>
          </Group>

          <Divider/>

          <Grid>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.vehicle.info.field.name.label", "Name")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                {vehicle.name}
              </Text>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.vehicle.info.field.company.label", "Company")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                {vehicle.companyId && <DefaultText value={vehicle.companyName}/>}
              </Text>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.vehicle.info.field.usable.label", "Usable")}
              </Text>
              <Text ta="left" c={vehicle.doNotUse ? "warning" : "dimmed"} fz="xs">
                {vehicle.doNotUse ? t("common.shared.no", "No") : t("common.shared.yes", "Yes")}
              </Text>
            </Grid.Col>
          </Grid>
        </Card>
      </Grid.Col>
      <Grid.Col span={6}>
        <VehicleCapabilityCard vehicleId={vehicle.id}/>
      </Grid.Col>
      <Grid.Col span={12}>
        <HasAccess permissions={["object_mapping_view"]}>
          <ObjectMappingDataTableCard entity={vehicle}/>
        </HasAccess>
      </Grid.Col>
    </Grid>
  );
};

const VehicleCapabilityCard = (props: { vehicleId: string }) => {
  const {vehicleId} = props;

  const {t} = useTranslation(["common"]);

  const selectCapabilities = useMemo(() => {
    return fleetAssetCapabilityTypesSelector();
  }, []);

  const {activeCapabilities, isActiveCapabilitiesLoading} = useGetVehicleCapabilitiesQuery(vehicleId, {
    selectFromResult: (result) => ({
      ...result,
      activeCapabilities: selectCapabilities(result),
      isActiveCapabilitiesLoading: result.isLoading,
    })
  });

  const {
    addCapabilities,
    removeCapabilities,
    isAddingCapabilities,
    isRemovingCapabilities,
  } = useVehicleMutation()

  const isCapabilitiesLoading = useMemo(() => {
    return isActiveCapabilitiesLoading || isAddingCapabilities || isRemovingCapabilities
  }, [isAddingCapabilities, isRemovingCapabilities, isActiveCapabilitiesLoading])

  const toggleCapabilities = useCallback((cap: FleetAssetCapabilityType) => {
    const caps: string[] = []
    caps.push(cap.code)

    const value = activeCapabilities.has(cap)
    if (value) {
      removeCapabilities(vehicleId, {capabilities: caps}, undefined)
    } else {
      addCapabilities(vehicleId, {capabilities: caps}, undefined)
    }
  }, [addCapabilities, removeCapabilities, vehicleId, activeCapabilities])

  return (
    <Card shadow="sm" padding="lg" radius="md" withBorder>
      <Group position="apart" pb="sm">
        <Title order={4}>{
          t("common.entity.vehicle.profile.info.section.capabilities.label", "Capabilities")}
        </Title>
      </Group>

      <Divider my="md"/>

      <FleetAssetCapabilityDataTable
        permPrefix="vehicle"
        selectedCapabilities={activeCapabilities}
        toggleCapability={toggleCapabilities}
        verticalSpacing="sm"
        withBorder
        records={fleetAssetCapabilityTypes}
        recordsPerPageOptions={PAGINATION_PAGE_SIZES}
        fetching={isCapabilitiesLoading}
      />
    </Card>
  )
}

